import axios from "axios";

// Nominatim resolves a reverse lookup at a "zoom" level — the default (18) is
// building level. A pin dropped somewhere the map has no street address for
// (open water, desert, an unmapped rural area) comes back empty at 18, which is
// what used to leave the form stuck on "Address not found". So when the precise
// lookup finds nothing we widen out to progressively coarser levels until the
// map can name the *closest* place it knows — that result is flagged
// `approximate` so the UI can say so and the card can still be saved.
const ZOOM_STEPS = [undefined, 10, 6];

// Every caller shares this module, so a stale in-flight response must never
// overwrite a newer one. The previous guard ("skip if a request is running")
// silently dropped the request that carried the real coordinates whenever the
// modal loaded its card while the first lookup was still open — the card then
// looked address-less even though the pin was fine.
let latestRequestId = 0;
// Moving the pin notifies us twice (the handler that moved it, and the effect
// that watches the coordinate fields). Collapse the duplicate rather than
// asking Nominatim the same question twice.
let inFlightKey = "";

const isCoord = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  !isNaN(parseFloat(value));

const reverseLookup = async (lat, lng, zoom) => {
  const response = await axios.get(
    "https://nominatim.openstreetmap.org/reverse",
    {
      params: {
        lat,
        lon: lng,
        format: "json",
        addressdetails: 1,
        ...(zoom !== undefined && { zoom }),
      },
    }
  );

  const data = response?.data;
  if (!data || data.error || !data.display_name) return null;
  return data;
};

// Shapes a Nominatim payload into the flat object the site visit modals send as
// LocationDetails.*. `approximate` marks a hit that came from a coarser zoom —
// the coordinates are still the user's, only the naming is the nearest place.
const buildAddress = (lat, lng, data, approximate) => {
  const responseAddress = data?.address || {};
  const district = responseAddress.district || responseAddress.county;
  // Not every country has a "state". Albania's top sub-national level comes
  // back as `county` ("Qarku i Tiranës"), which is why a perfectly resolved
  // Tirana address still complained that the map could not locate the state.
  // Walk the administrative hierarchy instead of insisting on one key.
  const state =
    responseAddress.state ||
    responseAddress.region ||
    responseAddress.province ||
    responseAddress.state_district ||
    responseAddress.county ||
    responseAddress.municipality;

  const label = responseAddress.municipality
    ? `${data.display_name}, Parish: ${responseAddress.municipality}`
    : data.display_name;

  return {
    Latitude: lat,
    Longitude: lng,
    ...(responseAddress.house_number && {
      houseNumber: responseAddress.house_number,
    }),
    ...(responseAddress.road && { road: responseAddress.road }),
    ...(responseAddress.city && { city: responseAddress.city }),
    ...(responseAddress.municipality && {
      municipality: responseAddress.municipality,
    }),
    ...(district && { district }),
    ...(responseAddress.postcode && { postcode: responseAddress.postcode }),
    ...(responseAddress.country && { country: responseAddress.country }),
    ...(responseAddress.country_code && {
      countryCode: responseAddress.country_code,
    }),
    // An exact hit fills `parish`/`state`, which is what the rest of the form
    // treats as "the map found this". A coarse hit fills the `closest*` fields
    // instead, so the UI can offer it as a fallback without pretending the
    // precise address was found.
    ...(approximate
      ? {
          approximate: true,
          closestAddress: label,
          ...(state && { closestState: state }),
        }
      : {
          ...(label && { parish: label }),
          ...(state && { state }),
        }),
  };
};

const getAddress = async (loading, setLoading, setAddress, lat, lng) => {
  // No usable pin yet (modal still loading its card, or a half-typed value) —
  // firing the lookup anyway is what produced the bogus "Address not found".
  if (!isCoord(lat) || !isCoord(lng)) return;

  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);
  const key = `${parsedLat},${parsedLng}`;
  if (key === inFlightKey) return;

  const requestId = ++latestRequestId;
  inFlightKey = key;
  setLoading(true);
  try {
    for (let i = 0; i < ZOOM_STEPS.length; i += 1) {
      const data = await reverseLookup(parsedLat, parsedLng, ZOOM_STEPS[i]);
      if (requestId !== latestRequestId) return; // a newer pin won
      if (data) {
        setAddress(buildAddress(parsedLat, parsedLng, data, i > 0));
        return;
      }
    }
    // Nothing at any zoom: keep the coordinates so the card can still be saved
    // with a manually entered state.
    setAddress({ Latitude: parsedLat, Longitude: parsedLng, notFound: true });
  } catch (err) {
    console.error(err);
    if (requestId !== latestRequestId) return;
    setAddress({ Latitude: parsedLat, Longitude: parsedLng, notFound: true });
  } finally {
    if (requestId === latestRequestId) {
      inFlightKey = "";
      setLoading(false);
    }
  }
};

export default getAddress;
